import {
  IconAdjustments,
  IconBell,
  IconHelpCircle,
  IconLanguage,
  IconLayoutDashboard,
  IconPower,
  IconUser,
} from '@tabler/icons-react-native';
import { useAtomValue } from 'jotai';
import React, { useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet } from 'react-native';
import { currentTutorialAtom, currentTutorialStepAtom, isLoggedInAtom } from 'app/atoms';
import { PartagePlusBadge } from 'app/components/partage-plus-badge';
import { ChatBubblesLight } from 'app/design/icons/chat-bubbles';
import { useNavigation } from 'app/hooks/navigation';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useHasPartagePlusSubscription } from 'app/hooks/use-has-partage-plus-subscription';
import { useLogoutRoutine } from 'app/hooks/use-logout-routine';
import { i18n } from 'app/localization/i18n';
import { NO_CHANNEL_SELECTED } from 'app/network/chat/channel';
import { useCountUnreadMessages } from 'app/network/chat/message';
import { useDeleteIdentity } from 'app/network/identity';
import { LoanType } from 'app/network/loan';
import { useUnreadNotificationsCount } from 'app/network/notification';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import { useMyProfile } from 'app/network/profile';
import { Button } from 'app/design/primitives/button';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { Box, Column, Row, Text, useTheme } from 'app/shopify';
import type { PreferenceNameToValue } from 'app/types/preferences';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { Event, track } from '../../analytics';
import { getI18nLanguage, supportedLanguages, type Language } from '../../language';
import { log } from '../../monitoring';
import { storage, StorageKey } from '../../storage';
import { DesktopLanguageOption } from './header-language-option';
import { DesktopProfileMenuItem } from './header-profile-menu-item';
import { iconSize } from './header-nav-item';
import { getCurrentRouteBranchKey, subscribeNavigationState } from './navigation-state';
import { navigationRef } from 'app/lib/root-navigation';
import type { Profile } from 'app/types/profile';
import { ListingType } from 'app/types/listing';
import type { TFunction } from 'i18next';
import { Tutorial, TutorialRef } from 'app/types/tutorial';

function renderDesktopHeaderNavIcon(icon: 'management' | 'messages' | 'notifications', color: string) {
  switch (icon) {
    case 'management':
      return <IconLayoutDashboard strokeWidth={ICON_STROKE_WIDTH} size={iconSize} color={color} />;
    case 'messages':
      return <ChatBubblesLight color={color} width={iconSize} height={iconSize} />;
    case 'notifications':
      return <IconBell strokeWidth={ICON_STROKE_WIDTH} size={iconSize} color={color} />;
  }
}

function DesktopHeaderNavButton({
  active,
  compact = false,
  icon,
  label,
  onPress,
  unreadCount,
}: {
  active: boolean;
  compact?: boolean;
  icon: 'management' | 'messages' | 'notifications';
  label: string;
  onPress: () => void;
  unreadCount?: number;
}) {
  const theme = useTheme();
  return (
    <Button
      accessibilityLabel={label}
      accessibilityRole="button"
      alignItems="center"
      height={46}
      justifyContent="center"
      minWidth={compact ? 44 : 86}
      onPress={onPress}
      paddingHorizontal={compact ? 'spacing.general.x2' : 'spacing.general.x3'}
      paddingVertical="spacing.general.x1"
      style={[
        { borderRadius: theme.borderRadii['radius.sm'] },
        active ? { backgroundColor: 'rgba(14, 14, 14, 0.06)' } : undefined,
      ]}
      variant="plain"
    >
      <Box alignItems="center" justifyContent="center" position="relative" style={{ marginBottom: 2 }}>
        {renderDesktopHeaderNavIcon(icon, theme.colors['white-label.header.on-header'])}
        {unreadCount ? (
          <Box
            alignItems="center"
            justifyContent="center"
            position="absolute"
            style={{
              backgroundColor: theme.colors['icon.error'],
              borderRadius: 9,
              height: 18,
              minWidth: 18,
              paddingHorizontal: 4,
              right: -8,
              top: -7,
            }}
          >
            <Text color="white-label.headerSubtext" fontSize={10} fontWeight="600" lineHeight={12} textAlign="center">
              {Math.min(unreadCount, 99)}
            </Text>
          </Box>
        ) : null}
      </Box>
      {compact ? null : (
        <Text
          color="white-label.header.on-header"
          fontSize={12}
          fontWeight={active ? '700' : '600'}
          letterSpacing={0}
          lineHeight={16}
          textAlign="center"
        >
          {label}
        </Text>
      )}
    </Button>
  );
}

function useProfileMenuState(
  currentTutorial: Tutorial | null | undefined,
  currentTutorialStep: number | null | undefined,
) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const tutorialStateKey = `${currentTutorial ?? 'none'}:${String(currentTutorialStep)}`;
  const [previousTutorialStateKey, setPreviousTutorialStateKey] = useState<string | null>(null);

  if (previousTutorialStateKey !== tutorialStateKey) {
    setPreviousTutorialStateKey(tutorialStateKey);
    if (currentTutorial === Tutorial.HomeExplore && currentTutorialStep === 3) setProfileMenuOpen(true);
  }

  return [profileMenuOpen, setProfileMenuOpen] as const;
}

export function HeaderRightDesktop({ compact = false }: { compact?: boolean }) {
  const navigation = useNavigation();
  const withAuthorize = useAuthorize();

  const { t: tManagment } = useTranslation('management');
  const { t: tAllChannels } = useTranslation('all-channels');
  const { t: tHeader } = useTranslation('header');
  const { t: tLanguage } = useTranslation('language');
  const { t: tNotifications } = useTranslation('notifications');
  const { t: tProfileOverview } = useTranslation('profile-overview');
  const { data: profile } = useMyProfile();
  const upsertLanguagePreference = useUpsertOnePreference('language');
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const currentTutorial = useAtomValue(currentTutorialAtom);
  const currentTutorialStep = useAtomValue(currentTutorialStepAtom);
  const currentRouteBranchKey = useSyncExternalStore(subscribeNavigationState, getCurrentRouteBranchKey, () => '');
  const { data: unreadMessages } = useCountUnreadMessages({ enabled: isLoggedIn });
  const { data: unreadNotifications } = useUnreadNotificationsCount({ enabled: isLoggedIn });
  const hasPaidSubscription = useHasPartagePlusSubscription();
  const logoutRoutine = useLogoutRoutine();

  const languageQuery = useOnePreference('language');
  const i18nL = getI18nLanguage();
  const languagePreference = languageQuery.data?.value ?? i18nL;
  const currentRouteNames = currentRouteBranchKey.split('>').filter(Boolean);

  const onLanguageChange = async (to: PreferenceNameToValue['language']) => {
    if (getI18nLanguage() === to) return;
    try {
      await upsertLanguagePreference.mutateAsync(to);
      await i18n.changeLanguage(to);
    } catch {
      log('An error occured during language change', { to });
    }
  };
  const [profileMenuOpen, setProfileMenuOpen] = useProfileMenuState(currentTutorial, currentTutorialStep);

  const handleLangSelect = (lang: Language) => {
    void onLanguageChange(lang);
  };

  async function completeLogout() {
    setProfileMenuOpen(false);
    const currentUserId = await storage.readString(StorageKey.CurrentUserId);
    track(Event.UserInitiatedLogout, { source: 'logout', userId: currentUserId ?? 'null' });
    await logoutRoutine();

    if (Platform.OS === 'web' && navigationRef.isReady()) {
      navigationRef.navigate(
        'main',
        {
          screen: 'billboard',
          params: { screen: 'all-listings', params: { activeTab: ListingType.offer, type: ListingType.offer } },
        },
        { pop: true },
      );
      return;
    }

    navigation.reset({ routes: [{ name: 'onboardingIntro' }] });
  }

  const { mutate: deleteIdentity, isPending: isLogoutPending } = useDeleteIdentity({
    onSuccess: completeLogout,
    onError: completeLogout,
  });

  const closeProfileMenu = () => setProfileMenuOpen(false);

  const isManagementActive = currentRouteBranchKey.includes('management');
  const isMessagesActive = currentRouteNames.includes('chat');
  const isNotificationsActive = currentRouteBranchKey.includes('notifications');

  return (
    <Row alignItems="center" flexShrink={0} gap="spacing.general.x2" style={{ marginLeft: 'auto' }}>
      <DesktopHeaderNavButton
        compact={compact}
        active={isManagementActive}
        icon="management"
        label={tManagment('tabBarLabel')}
        onPress={() =>
          withAuthorize(() =>
            navigation.navigate('management', { screen: 'my-loans', params: { type: LoanType.Lending } }),
          )
        }
      />

      <DesktopHeaderNavButton
        compact={compact}
        active={isMessagesActive}
        icon="messages"
        label={tAllChannels('tabBarLabel')}
        onPress={() =>
          withAuthorize(() =>
            navigation.navigate('chat', { screen: 'one-channel', params: { url: NO_CHANNEL_SELECTED } }, { pop: true }),
          )
        }
        unreadCount={unreadMessages?.unread_count}
      />

      <DesktopHeaderNavButton
        compact={compact}
        active={isNotificationsActive}
        icon="notifications"
        label={tNotifications('tabBarLabel')}
        onPress={() => withAuthorize(() => navigation.navigate('notificationsOverlay', undefined, { pop: true }))}
        unreadCount={unreadNotifications?.unread_count}
      />

      <Box position="relative" zIndex={30}>
        <Button
          accessibilityLabel={tProfileOverview('title')}
          accessibilityRole="button"
          alignItems="center"
          height={38}
          justifyContent="center"
          marginLeft="spacing.general.x2"
          variant="plain"
          width={38}
          onPress={() => setProfileMenuOpen((open) => !open)}
          style={profileMenuOpen ? { backgroundColor: 'rgba(0, 0, 0, 0.055)' } : undefined}
        >
          <Thumbnail
            source={profile?.picture ? { uri: profile.picture } : undefined}
            height={iconSize * 1.2}
            width={iconSize * 1.2}
          />
        </Button>

        <DesktopProfileMenu
          closeProfileMenu={closeProfileMenu}
          deleteIdentity={deleteIdentity}
          handleLangSelect={handleLangSelect}
          isLogoutPending={isLogoutPending}
          languagePreference={languagePreference}
          onNavigateToHelp={() => withAuthorize(() => navigation.navigate('help', undefined, { pop: true }))}
          onNavigateToMyPreferences={() =>
            withAuthorize(() => navigation.navigate('profile', { screen: 'my-preferences', initial: false }))
          }
          onNavigateToProfile={() =>
            withAuthorize(() => navigation.navigate('profile', { screen: 'profile-overview', initial: false }))
          }
          profile={profile}
          profileMenuOpen={profileMenuOpen}
          tHeader={tHeader}
          tLanguage={tLanguage}
          tProfileOverview={tProfileOverview}
          hasPaidSubscription={hasPaidSubscription}
        />
      </Box>
    </Row>
  );
}

interface DesktopProfileMenuProps {
  closeProfileMenu: () => void;
  deleteIdentity: () => void;
  handleLangSelect: (lang: Language) => void;
  hasPaidSubscription: boolean | undefined;
  isLogoutPending: boolean;
  languagePreference: string;
  onNavigateToHelp: () => void;
  onNavigateToMyPreferences: () => void;
  onNavigateToProfile: () => void;
  profile: Profile | undefined;
  profileMenuOpen: boolean;
  tHeader: TFunction;
  tLanguage: TFunction;
  tProfileOverview: TFunction;
}

function DesktopProfileMenu({
  closeProfileMenu,
  deleteIdentity,
  handleLangSelect,
  hasPaidSubscription,
  isLogoutPending,
  languagePreference,
  onNavigateToHelp,
  onNavigateToMyPreferences,
  onNavigateToProfile,
  profile,
  profileMenuOpen,
  tHeader,
  tLanguage,
  tProfileOverview,
}: DesktopProfileMenuProps) {
  const theme = useTheme();
  if (!profileMenuOpen) return null;

  return (
    <>
      <Button
        variant="plain"
        onPress={closeProfileMenu}
        style={{
          ...StyleSheet.absoluteFill,
          position: 'fixed' as 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9,
        }}
      >
        <Box />
      </Button>
      <Column
        backgroundColor="surface.default"
        borderRadius="radius.md"
        borderWidth={1}
        elevation={6}
        minWidth={336}
        paddingVertical="spacing.general.x3"
        position="absolute"
        right={0}
        shadowColor="black"
        shadowOffset={{ width: 0, height: 12 }}
        shadowOpacity={0.14}
        shadowRadius={26}
        top="100%"
        marginTop="spacing.general.x4"
        zIndex={10}
        style={{ borderColor: 'rgba(0, 0, 0, 0.08)' }}
      >
        <Column paddingHorizontal="spacing.general.x4" paddingVertical="spacing.general.x3">
          <Row alignItems="center" gap="spacing.general.x2" justifyContent="space-between">
            <Thumbnail source={profile?.picture ? { uri: profile.picture } : undefined} height={32} width={32} />
            <Column flex={1}>
              <Text fontSize={14} fontWeight="700" numberOfLines={1}>
                {profile?.firstName ?? tProfileOverview('title')}
              </Text>
            </Column>
            {hasPaidSubscription === undefined ? null : (
              <PartagePlusBadge plan={hasPaidSubscription ? 'premium' : 'free'} variant="small" />
            )}
          </Row>
        </Column>

        <Box
          height={1}
          marginHorizontal="spacing.general.x4"
          marginVertical="spacing.general.x3"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
        />

        <Column gap="spacing.general.x1" paddingHorizontal="spacing.general.x1" paddingVertical="spacing.general.x0,5">
          <DesktopProfileMenuItem
            icon={<IconUser strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['icon.primary']} />}
            label={tProfileOverview('title')}
            onPress={() => {
              closeProfileMenu();
              onNavigateToProfile();
            }}
          />
          <DesktopProfileMenuItem
            icon={<IconAdjustments strokeWidth={ICON_STROKE_WIDTH} size={21} color={theme.colors['icon.primary']} />}
            label={tProfileOverview('myPreferencesButtonLabel')}
            onPress={() => {
              closeProfileMenu();
              onNavigateToMyPreferences();
            }}
          />
        </Column>

        <Box
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
          height={1}
          marginHorizontal="spacing.general.x4"
          marginVertical="spacing.general.x3"
        />

        <Column paddingHorizontal="spacing.general.x1" paddingVertical="spacing.general.x0,5">
          <DesktopProfileMenuItem
            icon={<IconHelpCircle strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['icon.primary']} />}
            label={tHeader('helpButtonAccessibilityLabel')}
            onPress={() => {
              closeProfileMenu();
              onNavigateToHelp();
            }}
            tutorialRef={TutorialRef.HeaderHelpButton}
          />
        </Column>

        {supportedLanguages.length > 1 ? (
          <>
            <Box
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
              height={1}
              marginHorizontal="spacing.general.x4"
              marginVertical="spacing.general.x3"
            />
            <Column paddingBottom="spacing.general.x1" paddingHorizontal="spacing.general.x1">
              <Row
                alignItems="center"
                gap="spacing.general.x2"
                paddingHorizontal="spacing.general.x3"
                paddingTop="spacing.general.x0,5"
              >
                <Box alignItems="center" width={22} height={22} justifyContent="center">
                  <IconLanguage strokeWidth={ICON_STROKE_WIDTH} size={17} color={theme.colors['icon.primary']} />
                </Box>
                <Text fontSize={12} fontWeight="700" color="text.disabled">
                  {tLanguage('title')}
                </Text>
              </Row>
              <Row
                gap="spacing.general.x1"
                flexWrap="wrap"
                paddingTop="spacing.general.x2"
                paddingHorizontal="spacing.general.x3"
              >
                {supportedLanguages.map((lang) => (
                  <DesktopLanguageOption
                    key={lang}
                    isSelected={languagePreference === lang}
                    lang={lang}
                    onPress={() => handleLangSelect(lang)}
                  />
                ))}
              </Row>
            </Column>
          </>
        ) : null}

        <Box
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
          height={1}
          marginHorizontal="spacing.general.x4"
          marginVertical="spacing.general.x3"
        />
        <Column paddingHorizontal="spacing.general.x1" paddingVertical="spacing.general.x0,5">
          <DesktopProfileMenuItem
            destructive
            icon={<IconPower strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['text.error']} />}
            label={tProfileOverview('logoutButtonLabel')}
            loading={isLogoutPending}
            onPress={() => deleteIdentity()}
          />
        </Column>
      </Column>
    </>
  );
}
